import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import { CandidateDetail, JobPostingDetail } from '../types';
import { EmailFormValues } from './EmailForm';
import SendEmailModal, { SendEmailModalProps } from './SendEmailModal';

interface PartialSendEmailModalProps extends Omit<SendEmailModalProps, 'helmetTitle'> {
  candidateIds: CandidateDetail['id'][];
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (data: void, variables: EmailFormValues, context: unknown) => void | Promise<unknown>;
}

const PartialSendEmailModal = ({
  candidateIds,
  onSuccess,
  jobPostingId,
  ...props
}: PartialSendEmailModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const mutation = useAPIMutation(
    async (
      { message, attachments, subject, fromName, replyTo, enableReplyTo }: EmailFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      for (const candidateId of candidateIds) {
        await api.post('/email-communications/', {
          from_name: fromName,
          candidate: candidateId,
          job_posting: jobPostingId,
          subject,
          message,
          ...(enableReplyTo ? { reply_to: replyTo } : {}),
          type: 'COMMUNICATION',
          attachments: attachmentIds,
        });
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-history/');
      },
    }
  );

  return (
    <SendEmailModal
      title={
        candidateIds.length > 1 ? (
          <span>
            <Trans>Enviar e-mail para</Trans>{' '}
            <strong>
              {candidateIds.length} <Trans>candidato(s)</Trans>
            </strong>
          </span>
        ) : (
          <span>
            <Trans>Enviar e-mail</Trans>
          </span>
        )
      }
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      helmetTitle={
        candidateIds.length > 1
          ? `${t('Enviar e-mail para')} ${candidateIds.length} ${t('candidatos')}`
          : t('Enviar e-mail')
      }
      loading={mutation.isLoading}
      jobPostingId={jobPostingId}
      {...props}
    />
  );
};

export default PartialSendEmailModal;
